/**
 * The error envelope — requirement 4.5.
 *
 * One shape for every failure the API can return, with a machine-readable code that is part of
 * the contract. HTTP status is transport: it is derived from the code (`statusForErrorCode`
 * below) rather than chosen per route, so two handlers cannot disagree about what a 403 means.
 *
 * `ErrorCode` is a string-literal union rather than a free string so a client can `switch` over
 * it exhaustively and a code added later forces the call sites that care to handle it.
 */
import { z } from 'zod';
/**
 * Every code the system can emit, and the task that emits it. The list is closed on purpose: a
 * route that needs a code that is not here adds it here first, which is the review step that
 * keeps clients able to enumerate what they might receive.
 *
 * Deliberately absent: anything describing *why* a validation failed. That belongs in `details`,
 * per field, where a form can attach it to an input.
 */
export const errorCodes = [
    /** 400 — the request body, query, or params failed their schema. `details` carries the fields. */
    'validation_failed',
    /** 401 — no credential, or one that is expired, malformed, or tampered with (Task 12). */
    'unauthenticated',
    /** 403 — a valid session whose role does not permit the operation (Task 12). */
    'forbidden',
    /** 403 — a missing or mismatched double-submit CSRF token on an unsafe method (Task 12). */
    'csrf_token_invalid',
    /** 404 — no such route, or no such record. */
    'not_found',
    /** 405 — a non-idempotent method on the read-only public surface (Task 11). */
    'method_not_allowed',
    /** 409 — the write conflicts with the current state, e.g. a duplicate natural key. */
    'conflict',
    /** 413 — the body exceeded the size limit for its content type (Task 8). */
    'payload_too_large',
    /** 429 — a rate limit was reached (Task 8, and strictly on login in Task 12). */
    'rate_limited',
    /** 500 — an unhandled failure. The message is generic; the request id is how it is traced. */
    'internal_error',
    /** 503 — a dependency is unreachable, which is what `/api/health` reports on (Task 8). */
    'service_unavailable',
];
export const errorCodeSchema = z.enum(errorCodes);
/**
 * The transport status for each code, exhaustive by construction: `satisfies` fails to compile
 * if a code is added above without one, so there is no default branch to fall through.
 */
export const errorCodeStatuses = {
    validation_failed: 400,
    unauthenticated: 401,
    forbidden: 403,
    csrf_token_invalid: 403,
    not_found: 404,
    method_not_allowed: 405,
    conflict: 409,
    payload_too_large: 413,
    rate_limited: 429,
    internal_error: 500,
    service_unavailable: 503,
};
export const statusForErrorCode = (code) => errorCodeStatuses[code];
/**
 * One field that failed validation. `path` is dotted rather than an array so a form can key its
 * inputs by it directly — `direccion`, `contacto.telefono`, `items.0.cantidad`.
 */
export const fieldErrorSchema = z.object({
    path: z.string(),
    message: z.string(),
});
/**
 * The envelope. `requestId` is outside `error` because it identifies the request, not the
 * failure: it is the same value the structured log carries (Task 8), which is what makes a
 * screenshot of an error page traceable to a log line.
 */
export const apiErrorSchema = z.object({
    error: z.object({
        code: errorCodeSchema,
        message: z.string(),
        details: z.array(fieldErrorSchema).optional(),
    }),
    requestId: z.string().min(1),
});
//# sourceMappingURL=errors.js.map